import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Container,
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  Chip,
  Grid,
  Divider,
  CircularProgress,
  Alert,
  Paper
} from '@mui/material';
import {
  Calendar,
  Clock,
  MapPin,
  User,
  ArrowLeft,
  CheckCircle,
  XCircle,
  ClockClockwise
} from '@phosphor-icons/react';
import api from '../../utils/api';
import useThemeStyles from '../../theme/useThemeStyles';
import { getBookingGuestDisplayName, getBookingGuestEmailLine } from '../../utils/unifiedBookingDisplay';
import SupplierBookingPendingActions from './SupplierBookingPendingActions';

const BookingDetail = () => {
  const styles = useThemeStyles();
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (id) {
      fetchBooking();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- verified intentional deps
  }, [id]);

  const fetchBooking = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/api/unified-bookings/${id}`);
      setBooking(response.data);
    } catch (error) {
      setError('Failed to load booking details');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return 'warning';
      case 'confirmed':
        return 'success';
      case 'rejected':
        return 'error';
      case 'cancelled':
        return 'error';
      case 'completed':
        return 'info';
      case 'rescheduled':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending':
        return <ClockClockwise size={20} weight="regular" />;
      case 'confirmed':
        return <CheckCircle size={20} weight="regular" />;
      case 'rejected':
        return <XCircle size={20} weight="regular" />;
      case 'cancelled':
        return <XCircle size={20} weight="regular" />;
      case 'completed':
        return <CheckCircle size={20} weight="regular" />;
      case 'rescheduled':
        return <ClockClockwise size={20} weight="regular" />;
      default:
        return <Clock size={20} weight="regular" />;
    }
  };

  const formatDateTime = (date) => {
    return new Date(date).toLocaleString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const isSupplierBookingDetail = location.pathname.includes('/supplier/bookings/');
  const showSupplierPendingActions = isSupplierBookingDetail && booking?.status === 'pending';

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
        <Button
          variant="outlined"
          startIcon={<ArrowLeft size={20} />}
          onClick={() => navigate(-1)}
        >
          Go Back
        </Button>
      </Container>
    );
  }

  if (!booking) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="info" sx={{ mb: 3 }}>
          Booking not found
        </Alert>
        <Button
          variant="outlined"
          startIcon={<ArrowLeft size={20} />}
          onClick={() => navigate(-1)}
        >
          Go Back
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Button
          variant="outlined"
          startIcon={<ArrowLeft size={20} />}
          onClick={() => navigate(-1)}
          sx={{ mb: 2 }}
        >
          Go Back
        </Button>
        <Typography component="h2" className="pagename" sx={styles.text.pagename}>
          Booking Details
        </Typography>
      </Box>

      {/* Booking Card */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
            <Box>
              <Typography variant="h5" gutterBottom>
                {booking.serviceType?.name || 'Service'}
              </Typography>
              <Typography variant="body1" color="text.secondary">
                with {booking.business?.name || 'Business'}
              </Typography>
            </Box>
            <Chip
              icon={getStatusIcon(booking.status)}
              label={booking.status.charAt(0).toUpperCase() + booking.status.slice(1)}
              color={getStatusColor(booking.status)}
              variant="outlined"
            />
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Booking Information */}
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2, bgcolor: 'background.default' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Calendar size={20} weight="regular" style={{ marginRight: 8 }} />
                  <Typography variant="subtitle2" color="text.secondary">
                    Date & Time
                  </Typography>
                </Box>
                <Typography variant="body1">
                  {formatDateTime(booking.startTime)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Duration: {booking.serviceType?.duration || 0} minutes
                </Typography>
              </Paper>
            </Grid>

            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2, bgcolor: 'background.default' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <MapPin size={20} weight="regular" style={{ marginRight: 8 }} />
                  <Typography variant="subtitle2" color="text.secondary">
                    Location
                  </Typography>
                </Box>
                <Typography variant="body1">
                  {booking.location?.type === 'on_site' ? 'On-site' : 
                   booking.location?.type === 'off_site' ? 'Off-site' : 
                   booking.location?.type === 'virtual' ? 'Virtual' : 'Multiple'}
                </Typography>
                {booking.location?.address && (
                  <Typography variant="body2" color="text.secondary">
                    {booking.location.address}
                  </Typography>
                )}
                {booking.location?.type === 'virtual' && booking.location?.videoLink && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    <a href={booking.location.videoLink} target="_blank" rel="noopener noreferrer">
                      Join video call
                    </a>
                  </Typography>
                )}
              </Paper>
            </Grid>

            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2, bgcolor: 'background.default' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <User size={20} weight="regular" style={{ marginRight: 8 }} />
                  <Typography variant="subtitle2" color="text.secondary">
                    {isSupplierBookingDetail ? 'Couple' : 'Client'}
                  </Typography>
                </Box>
                <Typography variant="body1">
                  {getBookingGuestDisplayName(booking)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {getBookingGuestEmailLine(booking)}
                </Typography>
              </Paper>
            </Grid>

            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2, bgcolor: 'background.default' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Clock size={20} weight="regular" style={{ marginRight: 8 }} />
                  <Typography variant="subtitle2" color="text.secondary">
                    Booking Type
                  </Typography>
                </Box>
                <Typography variant="body1">
                  {booking.isPublicEmbedBooking
                    ? 'Form Embed'
                    : booking.isManualBooking
                      ? 'Manual booking'
                      : 'Online booking'}
                </Typography>
                {booking.isPublicEmbedBooking && (
                  <Typography variant="body2" color="text.secondary">
                    Booked via your public link. Guest details are stored on the booking.
                  </Typography>
                )}
                {booking.isManualBooking && !booking.isPublicEmbedBooking && booking.manualBookingDetails && (
                  <Typography variant="body2" color="text.secondary">
                    Created by supplier
                  </Typography>
                )}
                {booking.publicEmbedLeadAmbiguous ? (
                  <Alert severity="warning" sx={{ mt: 2 }}>
                    More than one couple in your pipeline shares this email. The booking was not auto-linked. Link it to the right couple in My Couples.
                  </Alert>
                ) : null}
              </Paper>
            </Grid>
          </Grid>

          {showSupplierPendingActions && (
            <>
              <Divider sx={{ my: 3 }} />
              <SupplierBookingPendingActions
                booking={booking}
                onBookingUpdated={setBooking}
              />
            </>
          )}

          {isSupplierBookingDetail && booking.clientNotes && (
            <>
              <Divider sx={{ my: 3 }} />
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Notes from couple
                </Typography>
                <Typography variant="body1">
                  {booking.clientNotes}
                </Typography>
              </Box>
            </>
          )}

          {isSupplierBookingDetail && booking.supplierNotes && (
            <>
              <Divider sx={{ my: 3 }} />
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Internal notes
                </Typography>
                <Typography variant="body1">
                  {booking.supplierNotes}
                </Typography>
              </Box>
            </>
          )}
        </CardContent>
      </Card>

    </Container>
  );
};

export default BookingDetail;
